// ---------------------------------------------------------------------------
// DUMMY payment data for the "Details of payment" screens.
//
// There is no payments backend yet. Everything here is derived on the client
// from the flat-details record (ExtraFlatSchemeII etc.) so the UI can be
// tested end to end. Receipt uploads use the real /upload/image API, but the
// link between a receipt and an instalment is only kept in localStorage.
// Replace this module with real API calls when the backend exists.
// ---------------------------------------------------------------------------

// Rate charged per extra sq.ft (dummy)
export const EXTRA_AREA_RATE = 4500

// When true, some flats with no ExtraFlatSchemeII value also get a dummy
// extra area so there is always test data. Set to false for production.
export const DEMO_MODE = true
const DEMO_EXTRA_AREA_SQFT = 120

// Instalment schedule as share of the total extra-area amount
const SCHEDULE = [
  { id: 'booking', label: 'Booking amount', share: 0.2 },
  { id: 'plinth', label: 'On completion of plinth', share: 0.3 },
  { id: 'slab', label: 'On completion of slabs', share: 0.3 },
  { id: 'possession', label: 'On possession', share: 0.2 },
]

const DAY = 24 * 60 * 60 * 1000
const RECEIPTS_KEY = 'bl-payment-receipts'
export const RECEIPTS_CHANGED_EVENT = 'bl-receipts-changed'

const toNumber = value => {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) && n > 0 ? n : 0
}

const isEmptyValue = value => {
  const v = String(value ?? '').trim().toLowerCase()
  return !v || v === 'na' || v === 'n/a' || v === 'no' || v === '-' || v === '0'
}

// Extra area (sq.ft) for a flat, or 0 when the flat has no extra-flat scheme
export const getExtraAreaSqft = flat => {
  if (!flat) return 0
  const raw = flat.ExtraFlatSchemeII

  if (!isEmptyValue(raw)) {
    // Numeric value = extra area itself; any other value (e.g. "Yes") means
    // eligible, so fall back to the scheme's additional / difference area.
    return (
      toNumber(raw) ||
      toNumber(flat.AdditionalAreaAsPerSchemeI7Minus5) ||
      toNumber(flat.DifferenceInAreaSqft6Minus5) ||
      DEMO_EXTRA_AREA_SQFT
    )
  }

  if (DEMO_MODE && Number(flat.id ?? flat.ID) % 3 === 0) return DEMO_EXTRA_AREA_SQFT
  return 0
}

export const hasExtraFlat = flat => getExtraAreaSqft(flat) > 0

// ---- receipts (localStorage, dummy persistence) ----
const readReceipts = () => {
  try {
    return JSON.parse(localStorage.getItem(RECEIPTS_KEY)) || {}
  } catch (e) {
    return {}
  }
}

export const getReceipt = (flatId, instalmentId) => readReceipts()[`${flatId}:${instalmentId}`] || null

export const saveReceipt = (flatId, instalmentId, receipt) => {
  const all = readReceipts()
  all[`${flatId}:${instalmentId}`] = receipt
  try {
    localStorage.setItem(RECEIPTS_KEY, JSON.stringify(all))
  } catch (e) {
    // storage full / blocked — receipt still shows until reload
  }
  // let other widgets (sidebar badge, alerts) re-evaluate
  window.dispatchEvent(new Event(RECEIPTS_CHANGED_EVENT))
}

// Deterministic dummy status pattern per flat so different users see
// different situations: due soon, overdue, or fully up to date.
const scenarioFor = flatId => Number(flatId) % 3 // 0: overdue, 1: due soon, 2: up to date

export const buildPaymentPlan = flat => {
  const flatId = flat?.id ?? flat?.ID
  const extraAreaSqft = getExtraAreaSqft(flat)
  if (!extraAreaSqft) return null

  const total = Math.round(extraAreaSqft * EXTRA_AREA_RATE)
  const scenario = scenarioFor(flatId)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Offsets (days from today) for each instalment's due date
  const offsets =
    scenario === 0 ? [-120, -10, 50, 140]   // 2nd instalment overdue
    : scenario === 1 ? [-90, 5, 65, 160]    // 2nd instalment due in 5 days
    : [-150, -60, 30, 120]                  // paid up, next in 30 days

  const paidCount = scenario === 2 ? 2 : 1

  const instalments = SCHEDULE.map((step, i) => {
    const amount = Math.round(total * step.share)
    const dueDate = new Date(today.getTime() + offsets[i] * DAY)
    const receipt = getReceipt(flatId, step.id)

    let status
    if (i < paidCount) status = 'paid'
    else if (receipt) status = 'review'
    else if (dueDate < today) status = 'overdue'
    else if (dueDate - today <= 15 * DAY) status = 'due'
    else status = 'upcoming'

    return { ...step, amount, dueDate, status, receipt }
  })

  const paid = instalments.filter(x => x.status === 'paid').reduce((s, x) => s + x.amount, 0)
  const nextDue = instalments.find(x => x.status === 'overdue' || x.status === 'due') || null

  return {
    flatId,
    extraAreaSqft,
    rate: EXTRA_AREA_RATE,
    total,
    paid,
    balance: total - paid,
    instalments,
    nextDue,
  }
}

export const formatINR = value =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value || 0)

export const formatDate = date =>
  new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

export const daysUntil = date => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((new Date(date) - today) / DAY)
}

// Map a raw /flat-details record to the shape used above
export const normalizeFlat = item =>
  item
    ? {
        id: item.ID,
        name: item.MembersName?.trim() || '',
        room: `${item.BldgNo || ''} / ${item.FlatNo || ''}`,
        ExtraFlatSchemeII: item.ExtraFlatSchemeII,
        AdditionalAreaAsPerSchemeI7Minus5: item.AdditionalAreaAsPerSchemeI7Minus5,
        DifferenceInAreaSqft6Minus5: item.DifferenceInAreaSqft6Minus5,
      }
    : null
